<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcInvoiceReferences',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    invoice: {
      type: Object,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const ux = ref([]);

  const $data = computed(() => ({
    ux: ux.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'ux' }],
    loaders: {},
    ux: {},
    dataRefs: { ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const items = computed(() => {
    return props.invoice.references.data;
  });

  watch(
    () => props.invoice,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('invoice');
    }
  );

  const act_4044930568678877_emit_f75ioqpwv_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const act_4044930568678877_crud_esxhvq44c_getActionArgs = async ($event) => {
    return {
      objectType: 'invoice_reference',
      op: 'delete',
      data: {
        invoice_id: props.invoice.id,
        etag: props.invoice.etag,
        reference_type: $event.scope.item.object,
        reference_id: $event.scope.item.id,
      },
    };
  };
  const act_4044930568678877_crud_esxhvq44c_getConfirm = async ($event) => {
    return {
      message: $fn.value.gettext('Please confirm deleting the reference.'),
      btnOk: $fn.value.gettext('Delete reference'),
      severity: 'warning',
    };
  };
  const act_8358196963355853_emit_vx5ln7j01_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const inst_evh_8358196963355853_close_zmq7llha0_getWhen = async ($event) => {
    return !!$event.data.modalResult && !!$event.data.modalResult.ok;
  };
  const act_8358196963355853_showModal_f3zk1r7pp_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'InvoiceReferenceSearch',
      props: {
        invoiceId: props.invoice.id,
      },
    };
  };

  const act_4044930568678877_emit_f75ioqpwv = new actionTypes.EmitAction({
    actionArgs: act_4044930568678877_emit_f75ioqpwv_getActionArgs,
  });
  const inst_evh_4044930568678877_success_gxqyz35tz =
    new actionTypes.EventHandlerImpl({
      action: act_4044930568678877_emit_f75ioqpwv,
      event: 'success',
      displayName: 'emit',
    });
  const act_4044930568678877_crud_esxhvq44c = new actionTypes.CRUDAction({
    actionArgs: act_4044930568678877_crud_esxhvq44c_getActionArgs,
    confirm: act_4044930568678877_crud_esxhvq44c_getConfirm,
    events: [inst_evh_4044930568678877_success_gxqyz35tz],
  });
  const inst_evh_4044930568678877_click_hs4b623or =
    new actionTypes.EventHandlerImpl({
      action: act_4044930568678877_crud_esxhvq44c,
      event: 'click',
      displayName: 'crud',
    });
  const inst_evh_4044930568678877_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_4044930568678877_click_hs4b623or],
    event: 'click',
  });
  const act_8358196963355853_emit_vx5ln7j01 = new actionTypes.EmitAction({
    actionArgs: act_8358196963355853_emit_vx5ln7j01_getActionArgs,
  });
  const inst_evh_8358196963355853_close_zmq7llha0 =
    new actionTypes.EventHandlerImpl({
      action: act_8358196963355853_emit_vx5ln7j01,
      event: 'close',
      when: inst_evh_8358196963355853_close_zmq7llha0_getWhen,
      displayName: 'emit',
    });
  const act_8358196963355853_showModal_f3zk1r7pp =
    new actionTypes.ShowModalAction({
      actionArgs: act_8358196963355853_showModal_f3zk1r7pp_getActionArgs,
      events: [inst_evh_8358196963355853_close_zmq7llha0],
    });
  const inst_evh_8358196963355853_clickPrevent_evz5y86el =
    new actionTypes.EventHandlerImpl({
      action: act_8358196963355853_showModal_f3zk1r7pp,
      event: 'click.prevent',
      displayName: 'showModal',
    });
  const inst_evh_8358196963355853_clickPrevent =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_8358196963355853_clickPrevent_evz5y86el],
      event: 'click.prevent',
    });

  const actions = {
    inst_evh_4044930568678877_success_gxqyz35tz,
    inst_evh_4044930568678877_click_hs4b623or,
    inst_evh_4044930568678877_click,
    inst_evh_8358196963355853_close_zmq7llha0,
    inst_evh_8358196963355853_clickPrevent_evz5y86el,
    inst_evh_8358196963355853_clickPrevent,
  };

  const evh_4044930568678877_success_gxqyz35tz = async (event, scope) => {
    return await actions.inst_evh_4044930568678877_success_gxqyz35tz.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4044930568678877_click_hs4b623or = async (event, scope) => {
    return await actions.inst_evh_4044930568678877_click_hs4b623or.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4044930568678877_click = async (event, scope) => {
    return await actions.inst_evh_4044930568678877_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8358196963355853_close_zmq7llha0 = async (event, scope) => {
    return await actions.inst_evh_8358196963355853_close_zmq7llha0.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8358196963355853_clickPrevent_evz5y86el = async (event, scope) => {
    return await actions.inst_evh_8358196963355853_clickPrevent_evz5y86el.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8358196963355853_clickPrevent = async (event, scope) => {
    return await actions.inst_evh_8358196963355853_clickPrevent.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpBox direction="column">
    <div v-if="items && !items.length">
      {{ pgettext('InvoiceReferences', 'No references added.') }}
    </div>
    <ItpBox direction="column" v-if="items && items.length">
      <ItpBox
        :key="item.id"
        class="app-invoice-reference-item border rounded rounded-3 p-2 mb-2 w-100 overflow-hidden"
        v-for="item in items"
      >
        <InvoiceReferenceItem :item="item"> </InvoiceReferenceItem>
        <ItpLink
          icon="fa-times"
          class="ms-2 ms-auto"
          @click="evh_4044930568678877_click($event, { item })"
        >
        </ItpLink>
      </ItpBox>
    </ItpBox>
    <ItpLink
      :icon="$config.addIcon"
      :text="gettext('Add reference')"
      class="ms-auto"
      @click.prevent="evh_8358196963355853_clickPrevent($event, {})"
    >
    </ItpLink>
  </ItpBox>
</template>
